import { useState, useEffect, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import moment from "moment";

import { isProd } from "../../helpers/utilities";

import {
  useGetDealersInfo,
  useGetPotentialOrdersForReturn,
} from "./manualReturns.utils";

import { Input, Select } from "../Utility/FormElements";

const isPreviousSubmitDataSameAsLatest = (previous, latest) => {
  const isInvoiceNumberSame = previous?.invoiceNumber === latest?.invoiceNumber;

  const isStartDateSame = moment(previous?.startDate).isSame(
    moment(latest?.startDate).startOf("day").format()
  );

  const isEndDateSame = moment(previous?.endDate).isSame(
    moment(latest?.endDate).endOf("day").format()
  );

  return isInvoiceNumberSame && isStartDateSame && isEndDateSame;
};

const formatDealerInfo = (dealerInfo) => {
  if (!dealerInfo || !Array.isArray(dealerInfo) || dealerInfo?.length === 0) {
    return [];
  }

  const excludeDealerId = isProd() ? [0, 22622] : [0];

  const uniqueDealers = dealerInfo.filter(
    (dealer, index, self) =>
      index === self.findIndex((t) => t.dealerId === dealer.dealerId) &&
      !excludeDealerId.includes(dealer.dealerId)
  );

  const sortDealers = uniqueDealers.sort(
    (current, next) =>
      current.dealerName?.localeCompare(next?.dealerName || "") || 0
  );

  // remove dealers without dealerName,(null, undefined, empty string)

  return sortDealers.filter((dealer) => dealer.dealerName?.trim());
};

function InvoiceInput({
  register,
  errors,
  handleChangeInvoiceNumber,
  orderNotFound,
}) {
  if (!register) {
    throw new Error("register prop is required from useForm");
  }

  return (
    <div className="tw-w-full">
      <Input
        id="invoiceNumberSearch"
        label="Invoice Number"
        isError={!!errors?.invoiceNumber || orderNotFound}
        helperText={
          errors?.invoiceNumber?.message ||
          (orderNotFound &&
            "No order found for this invoice in the given date range")
        }
        {...register("invoiceNumber", {
          onChange: handleChangeInvoiceNumber,
          required: "Invoice number is required",
          minLength: {
            value: 1,
            message: "Invoice number should be at least 1 character",
          },
          maxLength: {
            value: 13,
            message: "Invoice number should not exceed 13 characters",
          },
        })}
        autoComplete="off"
      />
    </div>
  );
}

function CreateFromDateInput({ register, errors, getValues, orderNotFound }) {
  if (!register && !getValues) {
    throw new Error("register & getValues prop is required from useForm");
  }

  return (
    <div className="tw-w-full">
      <Input
        id="createFromDateSearch"
        label="Delivery From"
        type="date"
        isError={!!errors?.startDate || orderNotFound}
        helperText={errors?.startDate?.message}
        {...register("startDate", {
          required: "Delivery From is required",
          min: {
            value: moment().subtract(90, "days").format("YYYY-MM-DD"),
            message: "Delivery From date should be within last 90 days",
          },
          max: {
            value: moment().format("YYYY-MM-DD"),
            message: "Delivery From date should be today or earlier",
          },
          validate: (value) => {
            const endDate = getValues("endDate");

            if (moment(value).isAfter(endDate)) {
              return "Delivery From date should be before Delivery To date";
            }
          },
        })}
      />
    </div>
  );
}

function CreateToDateInput({ register, errors, getValues, orderNotFound }) {
  if (!register && !getValues) {
    throw new Error("register prop is required from useForm");
  }

  return (
    <div className="tw-w-full">
      <Input
        id="createToDateSearch"
        label="Delivery To"
        type="date"
        isError={!!errors?.endDate || orderNotFound}
        helperText={errors?.endDate?.message}
        {...register("endDate", {
          required: "Delivery To is required",
          min: {
            value: moment().subtract(90, "days").format("YYYY-MM-DD"),
            message: "Delivery To date should be within last 90 days",
          },
          max: {
            value: moment().add(7, "days").format("YYYY-MM-DD"),
            message:
              "Delivery To date should not be over 7 days into the future",
          },
          validate: (value) => {
            const startDate = getValues("startDate");

            if (moment(value).isBefore(startDate)) {
              return "Delivery To date should be after Delivery From date";
            }
          },
        })}
      />
    </div>
  );
}

function DealerSelect({
  control,
  register,
  errors,
  dealerInfo,
  previousSubmitData,
}) {
  if (!control || !register || !errors) {
    throw new Error("Missing required props");
  }

  const [latestInvoiceNumber, latestStartDate, latestEndDate] = useWatch({
    control,
    name: ["invoiceNumber", "startDate", "endDate"],
  });

  const isDealerSelectDisabled = () => {
    if (!dealerInfo || !Array.isArray(dealerInfo) || dealerInfo?.length === 0) {
      return true;
    }

    if (
      !isPreviousSubmitDataSameAsLatest(previousSubmitData, {
        invoiceNumber: latestInvoiceNumber,
        startDate: latestStartDate,
        endDate: latestEndDate,
      })
    ) {
      return true;
    }

    if (formatDealerInfo(dealerInfo).length === 0) {
      return true;
    }

    return false;
  };

  if (isDealerSelectDisabled()) {
    return (
      <div className="tw-w-full">
        <Select disabled label="Dealer" id="dealerIdSearchFallback">
          <option value="">Select Dealer</option>
        </Select>
      </div>
    );
  }

  return (
    <div className="tw-w-full">
      <Select
        id="dealerIdSearch"
        label="Dealer"
        isError={!!errors?.dealerId}
        helperText={errors?.dealerId?.message}
        {...register("dealerId", {
          required: "Dealer is required",
        })}
      >
        <option value="">Select Dealer</option>

        {formatDealerInfo(dealerInfo).map((dealer) => (
          <option key={dealer.dealerId} value={dealer.dealerId}>
            {dealer?.dealerId} {dealer.dealerName}
          </option>
        ))}
      </Select>
    </div>
  );
}

function SearchForm({ handleSearch }) {
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
    control,
    resetField,
    setValue,
    setError,
  } = useForm({
    defaultValues: {
      invoiceNumber: "",
      startDate: moment().subtract(6, "weeks").format("YYYY-MM-DD"),
      endDate: moment().format("YYYY-MM-DD"),
      dealerId: "",
    },
  });

  const setValueRef = useRef(setValue);
  const setErrorRef = useRef(setError);

  const [dealerInfoRequest, setDealerInfoRequest] = useState();
  const [manualReturnsRequest, setManualReturnsRequest] = useState();

  const {
    data: dealerInfoResponse,
    isInitialLoading: isInitialLoadingDealerInfo,
    isError: isErrorDealerInfo,
  } = useGetDealersInfo(dealerInfoRequest);

  const { lLoading: isInitialLoadingManualReturns } =
    useGetPotentialOrdersForReturn(manualReturnsRequest);

  useEffect(() => {
    if (isErrorDealerInfo) {
      toast.error("Error while fetching dealer information");
    }
  }, [isErrorDealerInfo]);

  const [orderNotFound, setOrderNotFound] = useState(false);
  const handleChangeInvoiceNumber = () => {
    setOrderNotFound(false);
  };

  useEffect(() => {
    const dealerInfo = formatDealerInfo(dealerInfoResponse?.data);

    if (!Array.isArray(dealerInfoResponse?.data)) {
      return;
    }

    if (
      (dealerInfoResponse?.data.length === 0 || dealerInfo?.length === 0) &&
      dealerInfoRequest
    ) {
      // toast.error("No order found with the given information");
      // setErrorRef?.current("invoiceNumber", {
      //   type: "manual",
      //   message: "No order found for this invoice in the given date range",
      // });
      setOrderNotFound(true);
    }

    if (
      dealerInfoResponse?.data.length === 1 &&
      dealerInfo?.length === 1 &&
      dealerInfoRequest
    ) {
      // console.log("Auto selecting dealer", dealerInfoResponse?.data[0]);
      // If only one dealer info is found then we can automatically select it
      const [dealer] = dealerInfoResponse?.data;

      // console.log('Auto selecting dealer', dealer);
      // setValue("dealerId", dealer.dealerId);
      setValueRef.current("dealerId", dealer.dealerId);

      const newManualReturnsRequest = {
        invoiceNumber: dealerInfoRequest?.invoiceNumber || "",
        startDate: dealerInfoRequest?.startDate || "",
        endDate: dealerInfoRequest?.endDate || "",
        dealerId: dealer.dealerId,
        dealerName: dealer.dealerName,
      };

      // We need to set the manualReturnRequest to get the orders
      setManualReturnsRequest(newManualReturnsRequest);
      handleSearch?.(newManualReturnsRequest);
    }

    // If multiple dealers are found then we need to show the select
    // display error if meaning they require to select a dealer
    if (dealerInfoResponse?.data.length > 1 && dealerInfoRequest) {
      // console.log("Multiple dealers found, user needs to select one");
      setErrorRef?.current("dealerId", {
        type: "required",
        message: "Need more information to proceed. Please select dealer",
      });
    }
  }, [dealerInfoResponse?.data, dealerInfoRequest, handleSearch]);

  const onSubmit = (submittedData) => {
    // If clientId is present then we need to find some orders containing the invoice numbers
    // Additionally, we need to check if the invoice number has changed or remained the same
    if (
      submittedData?.dealerId &&
      dealerInfoRequest &&
      isPreviousSubmitDataSameAsLatest(dealerInfoRequest, submittedData)
    ) {
      const newManualReturnsRequest = {
        invoiceNumber: submittedData.invoiceNumber,
        startDate: moment(submittedData.startDate).startOf("day").format(),
        endDate: moment(submittedData.endDate).endOf("day").format(),
        dealerId: submittedData.dealerId,
        dealerName: dealerInfoResponse?.data?.find(
          (dealer) => String(dealer.dealerId) === submittedData.dealerId
        )?.dealerName,
      };

      setManualReturnsRequest(newManualReturnsRequest);

      handleSearch?.(newManualReturnsRequest);

      return;
    }

    setDealerInfoRequest({
      invoiceNumber: submittedData.invoiceNumber,
      startDate: moment(submittedData.startDate).startOf("day").format(),
      endDate: moment(submittedData.endDate).endOf("day").format(),
    });

    resetField("dealerId");
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="tw-space-y-3 lg:tw-flex lg:tw-flex-row lg:tw-items-start lg:tw-gap-x-4 lg:tw-space-y-0"
    >
      {/* Invoice Input */}
      <InvoiceInput
        register={register}
        errors={errors}
        handleChangeInvoiceNumber={handleChangeInvoiceNumber}
        orderNotFound={orderNotFound}
      />

      {/* Created From Input */}
      <CreateFromDateInput
        register={register}
        errors={errors}
        getValues={getValues}
        orderNotFound={orderNotFound}
      />

      {/* Created To Input */}
      <CreateToDateInput
        register={register}
        errors={errors}
        getValues={getValues}
        orderNotFound={orderNotFound}
      />

      {/* Dealer Id */}
      {/* Refactor for performance reason */}
      <DealerSelect
        control={control}
        register={register}
        errors={errors}
        previousSubmitData={dealerInfoRequest}
        dealerInfo={dealerInfoResponse?.data}
      />

      <div
        className={`tw-text-center ${
          Object.keys(errors).length > 0 || orderNotFound
            ? "lg:tw-self-start"
            : "lg:tw-self-end"
        }`}
      >
        <button
          type="submit"
          className={`btn btn-primary ${
            Object.keys(errors).length > 0 || orderNotFound
              ? "lg:tw-mt-8"
              : "tw-mb-0.7"
          }`}
          disabled={isInitialLoadingDealerInfo || isInitialLoadingManualReturns}
        >
          Search
        </button>
      </div>
    </form>
  );
}

export default SearchForm;
